import { apiService } from "./apiService";
import {
  WorkflowTemplate,
  Workflow,
  ProcessingTask,
  WorkflowEmailSessionsResponse,
  WorkflowEmailRecipient,
} from "../types/workflow";

class WorkflowService {
  private baseUrl = "/api/hybrid";

  /**
   * Get all workflow templates with optional filters
   */
  async getTemplates(filters?: {
    category?: string;
    complexity?: string;
    integrations?: string[];
  }): Promise<WorkflowTemplate[]> {
    try {
      const params = new URLSearchParams();

      // Only add non-empty filter parameters
      if (filters?.category && filters.category.trim()) {
        params.append("category", filters.category);
      }
      if (filters?.complexity && filters.complexity.trim()) {
        params.append("complexity", filters.complexity);
      }
      if (filters?.integrations?.length) {
        params.append("integrations", filters.integrations.join(","));
      }

      console.log(
        "🔗 API call to:",
        `${this.baseUrl}/templates?${params.toString()}`
      );

      const response = await apiService.get(
        `${this.baseUrl}/templates?${params.toString()}`
      );

      console.log("📡 API response:", response);

      const templates = Array.isArray(response.data) ? response.data : [];
      console.log("📋 Templates processed:", templates.length, "templates");

      // If API returns empty or invalid data, fallback to mock
      if (templates.length === 0) {
        console.log("⚠️ API returned empty templates, using mock data");
        return this.getMockTemplates();
      }

      return templates;
    } catch (error) {
      console.error("❌ Error fetching templates from API:", error);
      console.log("🔄 Falling back to mock templates");
      // Return mock templates for development
      return this.getMockTemplates();
    }
  }

  private getMockTemplates(): WorkflowTemplate[] {
    return [
      {
        id: "engineering-metrics-weekly",
        name: "Weekly Engineering Metrics Report",
        description:
          "Automatically collect engineering metrics from Jira, GitHub, and Asana, analyze trends, and send weekly summary emails",
        category: "analysis",
        complexity: "complex",
        requirements: {
          pipedream: true,
          mcp: true,
          integrations: ["jira", "github", "asana"],
        },
        config: {
          steps: [],
          triggers: [
            {
              type: "schedule",
              config: { cron: "0 9 * * 1" },
            },
          ],
          variables: [
            {
              name: "selectedProjects",
              type: "array",
              source: "user_input",
              description: "Jira projects to include in metrics",
              required: true,
            },
            {
              name: "selectedRepos",
              type: "array",
              source: "user_input",
              description: "GitHub repositories to include in metrics",
              required: true,
            },
            {
              name: "selectedAsanaProjects",
              type: "array",
              source: "user_input",
              description: "Asana projects to include in metrics",
              required: false,
            },
          ],
        },
        estimatedExecutionTime: 45000,
      },
      {
        id: "investor-weekly-update",
        name: "Investor Weekly Update",
        description:
          "Automatically collect KPIs from Google Sheets, revenue data from Stripe, pipeline data from CRM, and goals from Notion to create comprehensive investor updates",
        category: "notification",
        complexity: "complex",
        requirements: {
          pipedream: true,
          mcp: true,
          integrations: [
            "google-sheets",
            "notion",
            "stripe",
            "salesforce",
            "slack",
          ],
        },
        config: {
          steps: [],
          triggers: [
            {
              type: "schedule",
              config: { cron: "0 9 * * 1" },
            },
          ],
          variables: [
            {
              name: "kpiSpreadsheetId",
              type: "string",
              source: "user_input",
              description: "Google Sheets ID containing KPI data",
              required: true,
            },
            {
              name: "kpiRange",
              type: "string",
              source: "user_input",
              description: "Range in the spreadsheet (e.g., 'KPIs!A1:E10')",
              required: true,
              defaultValue: "Sheet1!A1:E50",
            },
            {
              name: "goalsNotionDatabase",
              type: "string",
              source: "user_input",
              description: "Notion database ID for company goals",
              required: true,
            },
            {
              name: "investorUpdateChannels",
              type: "array",
              source: "user_input",
              description:
                "Slack channels to monitor for investor-relevant updates",
              required: false,
              defaultValue: ["general", "leadership"],
            },
          ],
        },
        estimatedExecutionTime: 75000,
      },
      {
        id: "stakeholder-product-progress",
        name: "Stakeholders Product Progress Report",
        description:
          "Generate comprehensive product progress reports by collecting data from Linear, Jira, Slack, and Notion to keep stakeholders aligned on execution and blockers",
        category: "analysis",
        complexity: "complex",
        requirements: {
          pipedream: true,
          mcp: true,
          integrations: ["linear", "jira", "slack", "notion"],
        },
        config: {
          steps: [],
          triggers: [
            {
              type: "schedule",
              config: { cron: "0 10 * * 1" },
            },
          ],
          variables: [
            {
              name: "selectedTeams",
              type: "array",
              source: "user_input",
              description: "Linear teams to include in the report",
              required: true,
            },
            {
              name: "selectedJiraProjects",
              type: "array",
              source: "user_input",
              description: "Jira projects to include in the report",
              required: false,
            },
            {
              name: "productChannels",
              type: "array",
              source: "user_input",
              description:
                "Slack channels to monitor for product updates and blockers",
              required: true,
              defaultValue: ["product", "engineering", "design"],
            },
            {
              name: "specsNotionDatabase",
              type: "string",
              source: "user_input",
              description: "Notion database ID for product specifications",
              required: false,
            },
          ],
        },
        estimatedExecutionTime: 60000,
      },
      {
        id: "integration-health-monitor",
        name: "Integration Health Monitor",
        description:
          "Monitor the health of your integrations and send alerts when issues are detected",
        category: "notification",
        complexity: "medium",
        requirements: {
          pipedream: true,
          mcp: true,
          integrations: ["slack", "email"],
        },
        config: {
          steps: [],
          triggers: [
            {
              type: "schedule",
              config: { cron: "*/30 * * * *" },
            },
          ],
          variables: [
            {
              name: "monitoredIntegrations",
              type: "array",
              source: "user_input",
              description: "List of integrations to monitor",
              required: true,
            },
          ],
        },
        estimatedExecutionTime: 15000,
      },
      {
        id: "customer-feedback-analyzer",
        name: "Customer Feedback Analyzer",
        description:
          "Collect customer feedback from multiple sources, analyze sentiment, and generate insights",
        category: "analysis",
        complexity: "complex",
        requirements: {
          pipedream: true,
          mcp: true,
          integrations: ["zendesk", "typeform", "twitter"],
        },
        config: {
          steps: [],
          triggers: [
            {
              type: "schedule",
              config: { cron: "0 10 * * 1" },
            },
          ],
          variables: [
            {
              name: "feedbackFormId",
              type: "string",
              source: "user_input",
              description: "Typeform feedback form ID",
              required: true,
            },
            {
              name: "brandMentions",
              type: "string",
              source: "user_input",
              description: "Twitter search query for brand mentions",
              required: true,
            },
          ],
        },
        estimatedExecutionTime: 60000,
      },
    ];
  }

  /**
   * Get templates compatible with user's integrations
   */
  async getCompatibleTemplates(
    integrations: string[],
    hasPipedream = false,
    hasMCP = false
  ): Promise<WorkflowTemplate[]> {
    try {
      const params = new URLSearchParams({
        integrations: integrations.join(","),
        pipedream: hasPipedream.toString(),
        mcp: hasMCP.toString(),
      });

      const response = await apiService.get(
        `${this.baseUrl}/templates/compatible?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching compatible templates:", error);
      throw error;
    }
  }

  /**
   * Get a specific template by ID
   */
  async getTemplate(templateId: string): Promise<WorkflowTemplate> {
    try {
      const response = await apiService.get(
        `${this.baseUrl}/templates/${templateId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching template:", error);
      throw error;
    }
  }

  /**
   * Create a workflow from a template
   */
  async createWorkflowFromTemplate(
    templateId: string,
    config: {
      name?: string;
      variables?: Record<string, any>;
      schedule?: {
        enabled: boolean;
        cron?: string;
      };
    }
  ): Promise<Workflow> {
    try {
      const response = await apiService.post(
        `${this.baseUrl}/templates/${templateId}/create`,
        config
      );
      return response.data;
    } catch (error) {
      console.error("Error creating workflow from template:", error);
      throw error;
    }
  }

  /**
   * Get user's workflows (placeholder - would integrate with existing API)
   */
  async getUserWorkflows(): Promise<Workflow[]> {
    try {
      // Fetch workflows from backend (pipedream router provides listing)
      const response = await apiService.get(`/api/pipedream/workflows`);

      // API shape: { success: boolean, data: Workflow[] }
      const payload = response?.data;
      const rawItems = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];

      // Normalize to frontend Workflow shape and provide safe defaults
      const normalized: Workflow[] = rawItems.map((item: any) => {
        const steps = Array.isArray(item?.config?.steps)
          ? item.config.steps
          : [];
        const triggers = Array.isArray(item?.config?.triggers)
          ? item.config.triggers
          : [];
        const variables = Array.isArray(item?.config?.variables)
          ? item.config.variables
          : [];
        // Preserve recipientIds and listIds from config
        const recipientIds = Array.isArray(item?.config?.recipientIds)
          ? item.config.recipientIds
          : [];
        const listIds = Array.isArray(item?.config?.listIds)
          ? item.config.listIds
          : [];

        const executionCount =
          typeof item?.execution_count === "number" ? item.execution_count : 0;
        const successCount =
          typeof item?.success_count === "number" ? item.success_count : 0;

        return {
          id: String(item.id),
          user_id: String(item.user_id),
          name: item.name || "Untitled Workflow",
          description: item.description || "",
          status: (item.status as any) || "active",
          config: {
            steps,
            triggers,
            variables,
            recipientIds,
            listIds,
          },
          template_id: item.template_id ?? undefined,
          created_at: item.created_at || new Date().toISOString(),
          updated_at:
            item.updated_at || item.created_at || new Date().toISOString(),
          // Backend uses last_execution; map it to last_executed_at expected by UI
          last_executed_at:
            item.last_executed_at || item.last_execution || undefined,
          execution_count: executionCount,
          success_count: successCount,
          contact_list_id: item.contact_list_id ?? null,
          schedule_config: item.schedule_config ?? null,
          schedule_enabled:
            typeof item.schedule_enabled === "boolean"
              ? item.schedule_enabled
              : false,
          next_execution: item.next_execution ?? null,
          timezone: item.timezone ?? null,
          workflow_type: item.workflow_type ?? null,
          notifications: item.notifications ?? null,
          selected_integrations: item.selected_integrations ?? null,
          selected_insights: item.selected_insights ?? null,
        };
      });

      return normalized;
    } catch (error) {
      console.error("Error fetching user workflows:", error);
      throw error;
    }
  }

  /**
   * Process a task using hybrid intelligence
   */
  async processTask(task: ProcessingTask): Promise<any> {
    try {
      const response = await apiService.post(`${this.baseUrl}/process`, task);
      return response.data;
    } catch (error) {
      console.error("Error processing task:", error);
      throw error;
    }
  }

  /**
   * Get processing recommendation for a task
   */
  async getProcessingRecommendation(
    task: Partial<ProcessingTask>
  ): Promise<any> {
    try {
      const response = await apiService.post(`${this.baseUrl}/recommend`, task);
      return response.data;
    } catch (error) {
      console.error("Error getting recommendation:", error);
      throw error;
    }
  }

  /**
   * Get task processing status
   */
  async getTaskStatus(taskId: string): Promise<any> {
    try {
      const response = await apiService.get(`${this.baseUrl}/status/${taskId}`);
      return response.data;
    } catch (error) {
      console.error("Error getting task status:", error);
      throw error;
    }
  }

  /**
   * Get processing analytics
   */
  async getAnalytics(): Promise<any> {
    try {
      const response = await apiService.get(`${this.baseUrl}/analytics`);
      // Backend responds with { success, data }. Unwrap to return analytics shape directly
      const payload = response?.data;
      return payload && typeof payload === "object" && "data" in payload
        ? (payload as any).data
        : payload;
    } catch (error) {
      console.error("Error fetching analytics:", error);
      throw error;
    }
  }

  /**
   * Validate a template configuration
   */
  async validateTemplate(
    template: WorkflowTemplate
  ): Promise<{ valid: boolean; errors: string[] }> {
    try {
      const response = await apiService.post(
        `${this.baseUrl}/templates/validate`,
        template
      );
      return response.data;
    } catch (error) {
      console.error("Error validating template:", error);
      throw error;
    }
  }

  /**
   * Get available integrations for the user
   */
  async getUserIntegrations(): Promise<string[]> {
    try {
      // This would integrate with your existing integrations API
      // For now, return common integrations including the new ones
      return [
        "jira",
        "github",
        "asana",
        "slack",
        "notion",
        "google-sheets",
        "stripe",
        "salesforce",
        "hubspot",
        "linear",
        "zendesk",
        "typeform",
        "twitter",
        "google_contacts",
      ];
    } catch (error) {
      console.error("Error fetching user integrations:", error);
      return [];
    }
  }

  /**
   * Execute a workflow manually
   */
  async executeWorkflow(workflowId: string, data?: any): Promise<any> {
    try {
      const response = await apiService.post(
        `${this.baseUrl}/execute/${workflowId}`,
        { data }
      );
      return response.data;
    } catch (error) {
      console.error("Error executing workflow:", error);
      throw error;
    }
  }

  /**
   * Update workflow configuration
   */
  async updateWorkflow(
    workflowId: string,
    config: Partial<Workflow>
  ): Promise<Workflow> {
    try {
      const response = await apiService.put(
        `${this.baseUrl}/workflows/${workflowId}`,
        config
      );
      return response.data;
    } catch (error) {
      console.error("Error updating workflow:", error);
      throw error;
    }
  }

  /**
   * Delete a workflow
   */
  async deleteWorkflow(workflowId: string): Promise<void> {
    try {
      await apiService.delete(`${this.baseUrl}/workflows/${workflowId}`);
    } catch (error) {
      console.error("Error deleting workflow:", error);
      throw error;
    }
  }

  /**
   * Get workflow execution history
   */
  async getWorkflowHistory(workflowId: string, limit = 50): Promise<any[]> {
    try {
      const response = await apiService.get(
        `${this.baseUrl}/workflows/${workflowId}/history?limit=${limit}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching workflow history:", error);
      throw error;
    }
  }

  /**
   * Generate insights for a custom workflow
   */
  async generateWorkflowInsights(payload: {
    name: string;
    description: string;
    contactListId: string;
    integrations: string[];
  }): Promise<{
    success: boolean;
    data?: {
      insights: Array<{
        id: string;
        title: string;
        description: string;
        type: string;
        integration: string;
        data: any;
        selected: boolean;
      }>;
      integrationData: Record<string, any>;
    };
    error?: string;
  }> {
    try {
      console.log("🔗 Generating workflow insights:", payload);

      const response = await apiService.post(
        `${this.baseUrl}/workflows/generate-insights`,
        payload
      );

      console.log("🔍 Insights generation response:", response.data);

      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error("❌ Insights generation error:", error);

      const errorResponse = (error as any)?.response?.data;
      const errorMessage =
        errorResponse?.error ||
        (error instanceof Error
          ? error.message
          : "Failed to generate insights");

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Generate email content for a workflow
   */
  async generateWorkflowEmail(payload: {
    templateId: string;
    name: string;
    description: string;
    schedule?: {
      enabled: boolean;
      cron?: string;
      timezone?: string;
    };
  }): Promise<{
    success: boolean;
    data?: {
      subject: string;
      body: any;
      integrationData?: Record<string, any>;
    };
    error?: string;
  }> {
    try {
      const response = await apiService.post(
        `${this.baseUrl}/workflows/generate-email`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error generating workflow email:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to generate email",
      };
    }
  }

  /**
   * Generate email for custom workflow using insight IDs
   */
  async generateCustomWorkflowEmail(payload: {
    type: string;
    name: string;
    description: string;
    contactListId: string;
    recipientIds?: string[]; // Individual recipient IDs as alternative to contactListId
    selectedInsights: Array<{
      id: string;
      title: string;
      description: string;
      type: string;
      integration: string;
      data: any;
      selected: boolean;
    }>; // Current backend expectation
    insightIds: string[]; // Future backend optimization
    schedule: any;
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      console.log("🔗 Generating custom workflow email:", payload);

      // Use the existing generate-email endpoint but with custom workflow payload
      const emailPayload = {
        // Omit templateId to indicate this is a custom workflow
        name: payload.name,
        description: payload.description,
        schedule: payload.schedule,
        // Add custom workflow specific fields
        type: "custom", // Backend checks for this field and will auto-detect "custom_insights" from selectedInsights array
        contactListId: payload.contactListId,
        recipientIds: payload.recipientIds, // Backend accepts this as alternative to contactListId
        selectedInsights: payload.selectedInsights, // Backend will see this and set workflowType = "custom_insights"
        insightIds: payload.insightIds, // Future backend optimization
      };

      console.log("📤 Sending email generation payload:", {
        name: emailPayload.name,
        description: emailPayload.description,
        type: emailPayload.type,
        contactListId: emailPayload.contactListId,
        recipientIds: emailPayload.recipientIds,
        insightsCount: emailPayload.selectedInsights?.length || 0,
        hasSchedule: !!emailPayload.schedule,
      });

      const response = await apiService.post(
        `${this.baseUrl}/workflows/generate-email`,
        emailPayload
      );

      console.log("📧 Email generation response:", response.data);

      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error("❌ Custom workflow email generation error:", error);
      
      // Return the actual error so we can see what's failing
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to generate email",
      };
    }
  }

  /**
   * Create custom workflow
   */
  async createCustomWorkflow(workflowData: any): Promise<any> {
    try {
      console.log("🔗 Creating custom workflow:", workflowData);

      // Use the existing workflows endpoint for creation
      // Structure the payload to indicate it's a custom workflow (no templateId)
      const workflowPayload = {
        name: workflowData.name,
        description: workflowData.description,
        type: "custom",
        contactListId: workflowData.contactListId,
        recipientIds: workflowData.recipientIds, // Individual contacts
        listIds: workflowData.listIds, // Selected contact lists
        selectedIntegrations: workflowData.selectedIntegrations,
        schedule: workflowData.schedule,
        notifications: workflowData.notifications,
        generatedEmail: workflowData.generatedEmail,
        selectedInsights: workflowData.selectedInsights, // Current backend expectation
        insightIds: workflowData.insightIds, // Future backend optimization
        next_execution: workflowData.next_execution, // Explicit next execution time
      };
      
      console.log("📅 Schedule being sent:", workflowData.schedule);

      const response = await apiService.post(
        `${this.baseUrl}/workflows`,
        workflowPayload
      );

      console.log("✅ Custom workflow created:", response);

      return response.data;
    } catch (error) {
      console.error("❌ Custom workflow creation error:", error);
      // Re-throw the error so the UI can handle it properly
      throw error;
    }
  }

  /**
   * Send workflow email immediately
   */
  async sendWorkflowEmailNow(payload: {
    contactListId?: string;
    recipientIds?: string[];
    listIds?: string[];
    emailContent: {
      subject: string;
      body: string | any;
    };
    workflowName: string;
  }): Promise<{
    success: boolean;
    data?: any;
    error?: string;
    errorCode?: string;
  }> {
    try {
      console.log("📧 Sending workflow email now:", {
        recipientIds: payload.recipientIds?.length || 0,
        listIds: payload.listIds?.length || 0,
        contactListId: payload.contactListId,
        workflowName: payload.workflowName,
      });

      const response = await apiService.post(
        `${this.baseUrl}/workflows/send-now`,
        payload
      );

      console.log("✅ Email sent successfully:", response.data);

      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error("❌ Email sending error:", error);

      const errorResponse = (error as any)?.response?.data;
      const errorMessage =
        errorResponse?.error ||
        (error instanceof Error ? error.message : "Failed to send email");
      const errorCode = errorResponse?.errorCode;

      return {
        success: false,
        error: errorMessage,
        errorCode,
      };
    }
  }

  /**
   * Retry failed email sends from a previous send session
   */
  async retryFailedEmails(payload: {
    sendSessionId: string;
    retryOnlyRetryableErrors?: boolean;
  }): Promise<{
    success: boolean;
    data?: any;
    error?: string;
    errorCode?: string;
  }> {
    try {
      console.log("🔄 Retrying failed emails:", payload);

      const response = await apiService.post(
        `${this.baseUrl}/workflows/retry-send`,
        payload
      );

      console.log("✅ Email retry completed:", response.data);

      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error("❌ Email retry error:", error);

      const errorResponse = (error as any)?.response?.data;
      const errorMessage =
        errorResponse?.error ||
        (error instanceof Error ? error.message : "Failed to retry emails");
      const errorCode = errorResponse?.errorCode;

      return {
        success: false,
        error: errorMessage,
        errorCode,
      };
    }
  }

  /**
   * Get user-friendly error message based on error code
   */
  getErrorMessage(errorCode?: string, defaultMessage?: string): string {
    const errorMessages: Record<string, string> = {
      GMAIL_NOT_CONNECTED:
        "Gmail is not connected. Please connect your Gmail account in the integrations section.",
      GMAIL_TOKEN_EXPIRED:
        "Your Gmail authentication has expired. Please re-authenticate your Gmail account.",
      GMAIL_TOKEN_VALIDATION_FAILED:
        "Unable to validate Gmail access. Please try reconnecting your Gmail account.",
      GMAIL_REQUIRES_AUTH:
        "Gmail requires re-authentication. Please reconnect your Gmail account in integrations.",
      GMAIL_INACTIVE:
        "Gmail integration is inactive. Please reconnect your Gmail account in integrations.",
      CONTACT_FETCH_FAILED:
        "Unable to fetch contacts from the selected list. Please try again.",
      NO_CONTACTS_FOUND:
        "No valid contacts found in the selected list. Please check your contact list.",
      AUTH_FAILED:
        "Email authentication failed. Please re-authenticate your Gmail account.",
      RATE_LIMITED:
        "Email rate limit reached. Please wait a few minutes and try again.",
      INVALID_EMAIL: "One or more email addresses are invalid.",
      NETWORK_ERROR:
        "Network error occurred. Please check your connection and try again.",
      RECIPIENT_ERROR:
        "Issue with recipient mailbox. Some emails may not be deliverable.",
      SESSION_NOT_FOUND:
        "Send session not found. This retry session may have expired.",
      RESULTS_FETCH_FAILED: "Unable to fetch previous send results for retry.",
      RETRY_INTERNAL_ERROR:
        "Internal error during retry. Please try again later.",
      INTERNAL_SERVER_ERROR: "Internal server error. Please try again later.",
      OAUTH_SUCCESS: "Authentication successful!",
    };

    return (
      errorMessages[errorCode || ""] ||
      defaultMessage ||
      "An unexpected error occurred."
    );
  }

  /**
   * Check if an error code indicates a retryable error
   */
  isRetryableError(errorCode?: string): boolean {
    const retryableErrors = ["RATE_LIMITED", "NETWORK_ERROR", "UNKNOWN_ERROR"];
    return retryableErrors.includes(errorCode || "");
  }

  /**
   * Check if an error code indicates an authentication issue
   */
  isAuthError(errorCode?: string): boolean {
    // OAUTH_SUCCESS is not an auth error - it's a success state
    if (errorCode === "OAUTH_SUCCESS") return false;

    const authErrors = [
      "GMAIL_NOT_CONNECTED",
      "GMAIL_TOKEN_EXPIRED",
      "GMAIL_TOKEN_VALIDATION_FAILED",
      "GMAIL_REQUIRES_AUTH",
      "GMAIL_INACTIVE",
      "AUTH_FAILED",
    ];
    return authErrors.includes(errorCode || "");
  }

  /**
   * Get email sessions sent by a specific workflow
   */
  async getWorkflowEmailSessions(
    workflowId: string,
    options?: { limit?: number; offset?: number }
  ): Promise<WorkflowEmailSessionsResponse> {
    try {
      const params = new URLSearchParams();
      if (options?.limit) params.append("limit", options.limit.toString());
      if (options?.offset) params.append("offset", options.offset.toString());

      const response = await apiService.get(
        `/api/pipedream/workflows/${workflowId}/emails?${params.toString()}`
      );

      return response.data.data;
    } catch (error) {
      console.error("Error fetching workflow email sessions:", error);
      throw error;
    }
  }

  /**
   * Get email session details with recipients (uses existing endpoint)
   */
  async getEmailSessionDetails(
    sessionId: string
  ): Promise<{
    session: any;
    results: WorkflowEmailRecipient[];
    counts: { sent: number; delivered: number; opened: number; failed: number };
    last_sent_at: string | null;
  }> {
    try {
      const response = await apiService.get(
        `/emailUpdates/sessions/${sessionId}`
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching email session details:", error);
      throw error;
    }
  }
}

export const workflowService = new WorkflowService();
